import axios from 'axios';

import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';

import React, { useState } from 'react';

import { toast } from 'react-toastify';

import { useAuth } from '@src/contexts/authContext';
import { nop, nopa, removeNullish } from '@src/utils';

const url = `${process.env.REACT_APP_API_URL}/v2/admin/scopes`;

const filter = createFilterOptions();

const disableAddProfession = !(
  process.env[
    'REACT_APP_FFLAGS.MASTER_DATA.LISTING_SCOPE.ALLOW_ADD_PROFESSION'
  ]?.toLowerCase() === 'true'
);

export const validProfessions = process.env[
  'REACT_APP_FFLAGS.MASTER_DATA.LISTING_SCOPE.PROFESSIONS'
]
  ? [
      ...new Set(
        process.env['REACT_APP_FFLAGS.MASTER_DATA.LISTING_SCOPE.PROFESSIONS']
          .split(',')
          .map((it) => it.trim().toLowerCase()),
      ),
    ]
  : ['doctor', 'clinical assistant', 'nurse'];

const init = {
  label: undefined,
  value: undefined,
  help: {
    title: undefined,
    description: undefined,
  },
  sort: undefined,
};

export default function AddListingScope({ open, setOpen, fetchData }) {
  const { auth } = useAuth();
  const [isLoading, setLoading] = useState(true);
  const [scopes, setScopes] = useState([]);
  const [profession, setProfession] = useState(null);
  const [data, setData] = useState(init);
  // const professionOptions = React.useMemo(() => {
  //   if (!scopes || !Array.isArray(scopes)) return [];
  //   return (
  //     scopes.map((it) => ({
  //       id: it._id,
  //       profession: it.profession,
  //     })) || []
  //   );
  // }, [scopes]);
  const currentProfessions = React.useMemo(() => {
    if (!scopes || !Array.isArray(scopes)) return [];
    return scopes.map((it) => it.profession);
  }, [scopes]);
  const professionOptions = React.useMemo(() => {
    return [...new Set([...currentProfessions, ...validProfessions])].map(
      (it, idx) => ({
        id: String(idx),
        profession: it,
      }),
    );
  }, [currentProfessions]);
  const isNewProfession = React.useMemo(
    () => currentProfessions.includes(profession),
    [profession, currentProfessions],
  );
  const disableToBeCreatedScope = React.useMemo(
    () =>
      profession?.profession &&
      !validProfessions.includes(profession.profession),
    [profession],
  );

  React.useEffect(() => {
    if (!open) {
      setProfession(null);
      setData(init);
      setScopes(null);
    }
  }, [open]);

  React.useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        if (auth?.idToken && open) {
          setLoading(true);
          const response = await axios.get(`${url}?with_deleted=1`, {
            headers: {
              Authorization: `Bearer ${auth.idToken}`,
            },
          });

          setScopes([...(response.data?.data || [])]);
        }
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    };

    fetchDataAsync();
  }, [auth?.idToken, open]);

  const handlePost = async () => {
    const formData = removeNullish({
      label: data?.label,
      value: data?.value,
      help:
        data?.help?.title || data?.help?.description
          ? {
              title: data?.help?.title,
              description: data?.help?.description,
            }
          : undefined,
      sort: data?.sort,
    });

    if (auth?.idToken && !!Object.values(formData).filter((i) => !!i).length) {
      await axios
        .post(`${url}/items/create`, formData, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.idToken}`,
          },
        })
        .then(async (response) => {
          const createdItem = response.data?.data;
          if (createdItem?._id) {
            setData({ ...response.data?.data });
            const currentScope = scopes?.find?.(
              (it) => it.profession === profession?.profession,
            );
            const associateCreatedItemFormData = {
              profession: profession?.profession,
              scopes: [createdItem._id],
            };

            if (!isNewProfession && currentScope) {
              associateCreatedItemFormData.scopes = [
                ...(currentScope.scopes?.map?.((it) => it._id) || []),
                createdItem._id,
              ];
              associateCreatedItemFormData.sort =
                currentScope.sort ?? undefined;
            }

            await axios
              .post(
                `${url}/create`,
                removeNullish(associateCreatedItemFormData),
                {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${auth.idToken}`,
                  },
                },
              )
              .then((_) => {
                fetchData();
                toast.success('Item created successfully', {
                  position: 'top-center',
                  autoClose: 2000,
                });
              })
              .catch((error) => {
                axios
                  .delete(`${url}/items/${createdItem._id}/delete`, {
                    headers: {
                      Authorization: `Bearer ${auth.idToken}`,
                    },
                  })
                  .then(nop)
                  .catch((err) => console.error(err));
                console.error(error.message);
                const errorMsg =
                  typeof error.response?.data?.errors === 'object'
                    ? Object.values(error.response.data.errors)[0]?.msg
                    : error.message;
                toast.error(`${errorMsg}`, {
                  position: 'top-center',
                  autoClose: 2000,
                });
              });
            return;
          }

          throw new Error('Failed to create item');
        })
        .catch((error) => {
          console.error(error.message);
          toast.error(`${error.response.data.message}`, {
            position: 'top-center',
            autoClose: 2000,
          });
        })
        .finally(() => {
          setLoading(false);
          setOpen(null);
        });
    }
  };

  return (
    <Dialog maxWidth='lg' fullwidth open={!!open} onClose={() => setOpen(null)}>
      <DialogTitle>Add new item</DialogTitle>
      <br />

      <DialogContent>
        <Box flex={1} mr={1}>
          {isLoading ? (
            <>
              <Skeleton
                variant='text'
                width={50}
                height={24}
                sx={{ mb: '8px' }}
              />
              <Skeleton
                variant='rectangular'
                width='600px'
                height={56}
                sx={{ borderRadius: '6px' }}
              />
            </>
          ) : (
            <>
              <Typography fontFamily='Poppins'>
                Profession <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Autocomplete
                id='profession-autocomplete'
                fullWidth
                disablePortal
                freeSolo={!disableAddProfession}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                value={profession ?? ''}
                options={professionOptions}
                onChange={(_, newValue) => {
                  if (typeof newValue === 'string') {
                    setProfession(newValue);
                  } else if (newValue?.inputValue) {
                    setProfession(newValue.inputValue);
                  } else {
                    setProfession(newValue);
                  }
                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);
                  const { inputValue } = params;

                  const isExisting = options.some(
                    (option) => inputValue === option.profession,
                  );
                  if (
                    !disableAddProfession &&
                    inputValue !== '' &&
                    !isExisting
                  ) {
                    filtered.push({
                      inputValue,
                      profession: `Add "${inputValue}"`,
                    });
                  }

                  return filtered;
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  if (option.inputValue) {
                    return option.inputValue;
                  }
                  return option.profession;
                }}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={key} {...optionProps}>
                      {option.profession}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    size='medium'
                    variant='outlined'
                    placeholder={
                      disableAddProfession
                        ? 'Select profession'
                        : 'Select or create profession'
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      },
                      mt: '5px',
                      mb: '10px',
                      width: '600px',
                    }}
                  />
                )}
              />
            </>
          )}

          <Typography fontFamily='Poppins'>
            Value <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            required
            size='medium'
            variant='outlined'
            placeholder='Enter value'
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
              },
              mt: '5px',
              mb: '10px',
              width: '600px',
            }}
            value={data?.value ?? null}
            onChange={(e) =>
              setData({
                ...data,
                value: e.target.value,
              })
            }
          />

          <Typography fontFamily='Poppins'>
            Label <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            required
            size='medium'
            variant='outlined'
            placeholder='Enter label'
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
              },
              mt: '5px',
              mb: '10px',
              width: '600px',
            }}
            value={data?.label ?? null}
            onChange={(e) =>
              setData({
                ...data,
                label: e.target.value,
              })
            }
          />

          <Typography fontFamily='Poppins'>Helper Title</Typography>
          <TextField
            fullWidth
            size='medium'
            variant='outlined'
            placeholder='Enter helper title'
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
              },
              mt: '5px',
              mb: '10px',
              width: '600px',
            }}
            value={data?.help?.title ?? null}
            onChange={(e) =>
              setData({
                ...data,
                help:
                  data?.help?.title || data?.help?.description
                    ? {
                        ...data.help,
                        title: e.target.value,
                      }
                    : {
                        title: e.target.value,
                      },
              })
            }
          />

          <Typography fontFamily='Poppins'>Helper Description</Typography>
          <TextField
            fullWidth
            size='medium'
            variant='outlined'
            placeholder='Enter helper description'
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
              },
              mt: '5px',
              mb: '10px',
              width: '600px',
            }}
            value={data?.help?.description ?? null}
            onChange={(e) =>
              setData({
                ...data,
                help:
                  data?.help?.title || data?.help?.description
                    ? {
                        ...data.help,
                        description: e.target.value,
                      }
                    : {
                        title: 'Untitled',
                        description: e.target.value,
                      },
              })
            }
          />

          <Typography fontFamily='Poppins'>List No</Typography>
          <TextField
            fullWidth
            size='medium'
            variant='outlined'
            placeholder='Enter sort'
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
              },
              mt: '5px',
              width: '600px',
            }}
            value={data?.sort ?? null}
            onChange={(e) =>
              setData({
                ...data,
                sort: e.target.value.replace?.(/\D/g, '')
                  ? Number(e.target.value.replace(/\D/g, ''))
                  : undefined,
              })
            }
          />
          <br />
          <br />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          sx={{ textTransform: 'none' }}
          onClick={() => {
            setOpen(null);
          }}
          color='secondary'
        >
          Cancel
        </Button>
        {disableToBeCreatedScope ? (
          <Tooltip title='The selected profession is disabled for any modification'>
            <Box display='inline-flex'>
              <Button
                disabled
                sx={{ textTransform: 'none' }}
                color='primary'
                onClick={nopa}
              >
                <Box display='inline-flex' alignItems='center'>
                  <span>Save</span>
                  <HelpOutlineRoundedIcon
                    sx={{ fontSize: '0.9rem', marginLeft: '4px' }}
                    placement='right'
                  />
                </Box>
              </Button>
            </Box>
          </Tooltip>
        ) : (
          <Button
            sx={{ textTransform: 'none' }}
            color='primary'
            onClick={() => {
              handlePost();
              setOpen(null);
            }}
          >
            {isLoading ? <CircularProgress /> : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
